import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  ACESSO_DA_OPERACAO,
  carregarAcessoAoCliente,
  carregarCarteira,
  carregarParticipacao,
  type AcessoAoCliente,
  type ClienteDaCarteira,
  type Participacao,
} from '../servicos/participacao';
import { useEmpresaCorrente, useIdentidade } from './useSessao';

/**
 * Os papéis da empresa escolhida em cada serviço. Só para quem não é admin: o
 * admin vê e opera tudo, e o menu dele não depende disto.
 */
export function useParticipacao(): UseQueryResult<Participacao, Error> {
  const empresa = useEmpresaCorrente();
  const identidade = useIdentidade();
  return useQuery({
    queryKey: ['participacao', empresa],
    queryFn: () => carregarParticipacao(empresa ?? ''),
    enabled: empresa !== null && identidade !== null && !identidade.isAdmin,
    staleTime: 5 * 60_000,
  });
}

/** A carteira do assessor escolhido — para quem ele simula e pede fechamento. */
export function useCarteiraDoAssessor(
  habilitada: boolean,
): UseQueryResult<ClienteDaCarteira[], Error> {
  const empresa = useEmpresaCorrente();
  return useQuery({
    queryKey: ['participacao', 'carteira', empresa],
    queryFn: () => carregarCarteira(empresa ?? ''),
    enabled: habilitada && empresa !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * O que a empresa escolhida pode fazer na tela de um cliente — assessor
 * (gerencia), fornecedor (acompanha). O admin não pergunta ao servidor.
 */
export function useAcessoAoCliente(clienteId: string | undefined): {
  readonly acesso: AcessoAoCliente | undefined;
  readonly carregando: boolean;
  readonly erro: Error | null;
} {
  const empresa = useEmpresaCorrente();
  const identidade = useIdentidade();
  const admin = identidade?.isAdmin === true;
  const consulta = useQuery({
    queryKey: ['acesso-ao-cliente', empresa, clienteId],
    queryFn: () => carregarAcessoAoCliente(empresa ?? '', clienteId ?? ''),
    enabled: !admin && empresa !== null && clienteId !== undefined,
    retry: false,
    staleTime: 5 * 60_000,
  });
  if (admin) return { acesso: ACESSO_DA_OPERACAO, carregando: false, erro: null };
  return { acesso: consulta.data, carregando: consulta.isPending, erro: consulta.error };
}
